import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLookups, useDirectory } from "@/lib/lookups";
import { useToast, useConfirm } from "@/components/ui";
import { Field, TextInput, TextArea, Select } from "@/components/fields";
import type { ProjectBundle } from "../ProjectDetail";

export function ProjectSettings({ bundle }: { bundle: ProjectBundle }) {
  const p = bundle.project;
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const { can } = useAuth();
  const { byCategory } = useLookups();
  const users = useDirectory();
  const { confirm, node } = useConfirm();
  const [f, setF] = useState({
    name: p.name, description: p.description ?? "", status: p.status, healthNote: p.healthNote ?? "",
    ownerId: p.ownerId ?? "", managerId: p.managerId ?? "", startDate: p.startDate ?? "", endDate: p.endDate ?? "",
    plannedBudget: String(p.plannedBudget ?? 0), currency: p.currency, ganttStart: p.ganttStart?.slice(0, 10) ?? "", ganttWeeks: String(p.ganttWeeks),
  });
  const [newPhase, setNewPhase] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["project", p.id] });

  const save = useMutation({
    mutationFn: () => api.put(`/projects/${p.id}`, {
      ...f, plannedBudget: Number(f.plannedBudget) || 0, ganttWeeks: Number(f.ganttWeeks) || 70,
    }),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ["projects"] }); toast.push("success", "Project saved"); },
  });
  const addPhase = useMutation({
    mutationFn: () => api.post(`/projects/${p.id}/phases`, { name: newPhase }),
    onSuccess: () => { setNewPhase(""); invalidate(); },
  });
  const delPhase = useMutation({
    mutationFn: (id: string) => api.del(`/projects/${p.id}/phases/${id}`),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) => api.post(`/projects/${p.id}/archive`, { archived }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); toast.push("success", "Updated"); nav("/projects"); },
  });

  return (
    <div className="max-w-2xl space-y-5">
      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold">Project details</h3>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <div className="sm:col-span-2"><Field label="Name"><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field></div>
          <div className="sm:col-span-2"><Field label="Description"><TextArea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
          <Field label="Status"><Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
            {byCategory("project_status").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select></Field>
          <Field label="Currency"><TextInput value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} /></Field>
          <Field label="Project Owner"><Select value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })}>
            <option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
          </Select></Field>
          <Field label="Project Manager"><Select value={f.managerId} onChange={(e) => setF({ ...f, managerId: e.target.value })}>
            <option value="">—</option>{users.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
          </Select></Field>
          <Field label="Start date"><TextInput type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
          <Field label="End date"><TextInput type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></Field>
          <Field label="Planned budget"><TextInput type="number" value={f.plannedBudget} onChange={(e) => setF({ ...f, plannedBudget: e.target.value })} /></Field>
          <Field label="Gantt weeks"><TextInput type="number" value={f.ganttWeeks} onChange={(e) => setF({ ...f, ganttWeeks: e.target.value })} /></Field>
          <Field label="Gantt start (first week)"><TextInput type="date" value={f.ganttStart} onChange={(e) => setF({ ...f, ganttStart: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label="Manager's status note" hint="Shown on the Overview tab"><TextArea value={f.healthNote} onChange={(e) => setF({ ...f, healthNote: e.target.value })} /></Field></div>
        </div>
        <button className="btn-primary mt-3" disabled={save.isPending} onClick={() => save.mutate()}>Save changes</button>
      </div>

      <div className="card p-4">
        <h3 className="mb-3 text-sm font-semibold">Phases</h3>
        <ul className="mb-3 space-y-1.5">
          {bundle.phases.map((ph) => (
            <li key={ph.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-sm">
              <span>{ph.seq}. {ph.name}</span>
              <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed" onClick={() => delPhase.mutate(ph.id)}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <TextInput placeholder="New phase name" value={newPhase} onChange={(e) => setNewPhase(e.target.value)} />
          <button className="btn-outline" disabled={!newPhase} onClick={() => addPhase.mutate()}><Plus size={15} /> Add</button>
        </div>
      </div>

      {can("projects.archive") && (
        <div className="card border-st-delayed/30 p-4">
          <h3 className="mb-1 text-sm font-semibold">Danger zone</h3>
          <p className="mb-3 text-xs text-ink-faint">Archiving hides the project from active lists. Data is kept and can be restored.</p>
          <button className="btn-danger" onClick={async () => {
            if (await confirm(p.isArchived ? "Restore project" : "Archive project", `${p.isArchived ? "Restore" : "Archive"} "${p.name}"?`, !p.isArchived)) {
              archive.mutate(!p.isArchived);
            }
          }}>
            <Archive size={15} /> {p.isArchived ? "Restore project" : "Archive project"}
          </button>
        </div>
      )}
      {node}
    </div>
  );
}
